<template>
  <v-row no-gutters>
    <v-col cols="12">
      <sidebar-row border-bottom v-if="measurementsFetched">
        <sidebar-measurements-list
          buttons-align="left"
          class="pb-5"
          :measurements="computedMeasurements"
          v-on="listeners"
        >
          <template #header.buttons>
            <base-rounded-button icon="mdi-plus" size="x-small" color="success" @click="measurementAction('add')" />
          </template>
        </sidebar-measurements-list>
      </sidebar-row>
      <main-loader v-else />
    </v-col>
    <main-dialog
      v-if="isMainDialogLoaded"
      :is-visible.sync="isMainDialogVisible"
      v-bind="mainDialogBind"
      @agree="measurementActionHandler"
    />
    <main-input-dialog
      v-if="isMainInputDialogLoaded"
      :is-visible.sync="isMainInputDialogVisible"
      v-bind="mainInputDialogBind"
      @agree="measurementActionHandler"
    />
    <main-large-dialog
      v-if="isMainLargeDialogLoaded"
      :is-visible.sync="isMainLargeDialogVisible"
      v-bind="mainLargeDialogBind"
      @agree="measurementActionHandler"
    >
      <template #body>
        <v-col cols="12">
          <v-row no-gutters class="pb-5">
            <v-col cols="4">
              <data-input v-model="dateRange" v-bind="dateRangeInput" />
            </v-col>
          </v-row>
          <chart-line
            v-bind="chartBind"
            ref="chart"
            :chart-title="currentMeasurement.verbose_name"
            :y-label="`[${currentMeasurement.measurement_unit}]`"
          />
        </v-col>
      </template>
    </main-large-dialog>
  </v-row>
</template>
<script>
import { call, get } from 'vuex-pathify';
import { debounce } from 'lodash';
import ChartLine from '@/components/ChartLine';
import DataInput from '@/components/DataInput';
import MainLoader from '@/components/MainLoader';
import SidebarMeasurementsList from '@/components/SidebarMeasurementsList';
import SidebarRow from '@/components/SidebarRow';
export default {
  name: 'ScadaMeasurements',
  components: {
    ChartLine,
    DataInput,
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
    MainLargeDialog: () => import('@/components/MainLargeDialog'),
    MainLoader,
    SidebarMeasurementsList,
    SidebarRow,
  },
  props: {
    attributes: {
      type: Array,
      default: () => {
        return [];
      },
    },
    featureId: {
      type: [Number, String],
      required: true,
    },
  },
  computed: {
    scadaDataSourceName: get('admin/modulesMapping@scada.datasource_name'),
    measurements: get('scada/scadaMeasurements@:featureId'),
    measurementUnits: get('layers/measurementUnits'),
    capitalizeAction() {
      return this.$_capitalize(this.action);
    },
    chartBind() {
      return {
        labels: this.currentChartLabels,
        data: this.currentChartData,
        loading: this.chartDataLoading,
      };
    },
    computedMeasurements() {
      const dotsMenuItemsDict = {
        currentAssigned: [{ name: 'unassign' }, { name: 'save' }, { name: 'delete' }],
        currentUnassigned: [{ name: 'assign' }, { name: 'save' }, { name: 'delete' }],
        seriesAssigned: [{ name: 'save' }, { name: 'delete' }],
        seriesUnassigned: [{ name: 'save' }, { name: 'delete' }],
      };
      const contentButtonsDict = {
        series: [{ icon: 'mdi-chart-line', size: 'x-small', color: 'rgba(0,145,234,1)', name: 'chart' }],
        current: [],
      };
      return (
        this.measurements?.map(measurement => {
          const { measure_type = 'current', measure_attributes } = measurement;
          return {
            ...measurement,
            dotsMenuItems:
              dotsMenuItemsDict[`${measure_type}${measure_attributes?.length ? 'Assigned' : 'Unassigned'}`] || [],
            contentButtons: contentButtonsDict[measure_type],
          };
        }) || []
      );
    },
    computedMeasurementUnits() {
      return this.$_getFormattedMeasurementUnits(this.measurementUnits);
    },
    dateRanges() {
      const dict = {
        today: this.$dayjs(),
        tomorrow: this.$dayjs().add(1, 'd'),
        yesterday: this.$dayjs().subtract(1, 'd'),
        dayBeforeYesterday: this.$dayjs().subtract(2, 'd'),
        currentWeekStart: this.$dayjs().startOf('w'),
        currentWeekEnd: this.$dayjs().endOf('w'),
        currentMonthStart: this.$dayjs().startOf('M'),
        currentMonthEnd: this.$dayjs().endOf('M'),
        currentYearStart: this.$dayjs().startOf('y'),
        currentYearEnd: this.$dayjs().endOf('y'),
      };

      Object.keys(dict).map(key => {
        dict[key] = dict[key].format('YYYY-MM-DD');
      });
      return dict;
    },
    dateRangeInput() {
      return {
        dataType: { name: 'daterange' },
        hideDetails: true,
        label: this.$i18n.t('sidebar.zdmLightingControl.dateRangeFromTo'),
        disabled: this.chartDataLoading,
        localeDateFormat: 'YYYY-MM-DD',
        returnType: 'date',
        defaultRanges: [
          {
            text: this.$i18n.t('sidebar.zdmLightingControl.today'),
            value: [this.dateRanges.today, this.dateRanges.tomorrow],
          },
          {
            text: this.$i18n.t('sidebar.zdmLightingControl.yesterday'),
            value: [this.dateRanges.yesterday, this.dateRanges.today],
          },
          {
            text: this.$i18n.t('sidebar.zdmLightingControl.thisWeek'),
            value: [this.dateRanges.currentWeekStart, this.dateRanges.currentWeekEnd],
          },
          {
            text: this.$i18n.t('sidebar.zdmLightingControl.thisMonth'),
            value: [this.dateRanges.currentMonthStart, this.dateRanges.currentMonthEnd],
          },
          {
            text: this.$i18n.t('sidebar.zdmLightingControl.thisYear'),
            value: [this.dateRanges.currentYearStart, this.dateRanges.currentYearEnd],
          },
        ],
      };
    },
    listeners() {
      const listeners = {
        ...this.$listeners,
      };
      for (const action of ['assign', 'delete', 'save', 'unassign']) {
        listeners[action] = measurement => {
          this['measurementAction'](action, measurement);
        };
      }
      for (const action of ['chart']) {
        listeners[action] = measurement => {
          this['measurementAction'](action, measurement);
        };
      }
      return listeners;
    },
    mainDialogBind() {
      return {
        loading: this.isMainDialogLoading,
        ...(this.isMainDialogVisible
          ? {
              title: this.$i18n.t(`dialog.title.${this.action}Measurement`),
              body: this.$i18n.t(`dialog.body.confirmMeasurement${this.capitalizeAction}`, {
                measurement: this.currentMeasurement.verbose_name,
              }),
              agreeText: `dialog.agree${this.capitalizeAction}`,
            }
          : {}),
      };
    },
    mainInputDialogBind() {
      return {
        loading: this.isMainInputDialogLoading,
        dialogWidth: '40%',
        ...(this.isMainInputDialogVisible
          ? {
              titleTranslation: this.$i18n.t(`dialog.title.${this.action}Measurement`),
              agreeText: `dialog.agree${this.capitalizeAction}`,
              inputs: this.measurementActionInputs,
            }
          : {}),
      };
    },
    mainLargeDialogBind() {
      return {
        loading: this.isMainLargeDialogLoading,
        dialogWidth: '70%',
        maxHeight: '90vh',
        minBodyHeight: '300px',
        headerShadow: true,
        ...(this.isMainLargeDialogVisible
          ? {
              title: this.$i18n.t(`dialog.title.${this.action}Measurement`),
              agreeText: `dialog.agree${this.capitalizeAction}`,
            }
          : {}),
      };
    },
    measurementActionInputs() {
      const measurementsActionsInputsDict = {
        add: [
          {
            name: 'external_name',
            translationPath: 'dialog.body.measurementSeriesIdentifier',
            cols: '12',
            rules: [v => !!v || v == '0' || 'rules.required'],
            dataType: {
              name: 'text',
            },
          },
          {
            name: 'verbose_name',
            translationPath: 'dialog.body.verboseName',
            cols: '12',
            rules: [v => !!v || v == '0' || 'rules.required'],
            dataType: {
              name: 'text',
            },
          },
          {
            name: 'measurement_parameter',
            translationPath: 'dialog.body.parameterType',
            cols: '12',
            dataType: {
              name: 'select',
            },
            items: this.measurementParameters,
            itemText: 'verbose_name',
            itemValue: 'name',
          },
          {
            name: 'measurement_unit',
            translationPath: 'dialog.body.measurementUnit',
            cols: '12',
            dataType: {
              name: 'text',
            },
            // items: this.computedMeasurementUnits,
            // itemText: 'text',
            // itemValue: 'text',
          },
          {
            name: 'measure_type',
            translationPath: 'dialog.body.measureType',
            cols: '12',
            rules: [v => !!v || v == '0' || 'rules.required'],
            dataType: {
              name: 'select',
            },
            items: [
              {
                value: 'current',
                text: this.$i18n.t(`dialog.body.${this.measureTypesDict['current']}`),
              },
              {
                value: 'series',
                text: this.$i18n.t(`dialog.body.${this.measureTypesDict['series']}`),
              },
            ],
          },
        ],
        assign: [
          {
            name: 'attribute',
            translationPath: 'dialog.body.column',
            subtitle: 'dialog.body.measurementColumnSubtitle',
            cols: '12',
            rules: [v => !!v || v == '0' || 'rules.required'],
            dataType: {
              name: 'select',
            },
            items: this.attributes,
            itemValue: 'name',
            itemText: 'verbose_name',
            align: 'center',
            rowClass: 'pt-7',
            leftCol: 5,
            rightCol: 5,
            spacer: true,
          },
        ],
        save: [
          {
            name: 'external_name',
            translationPath: 'dialog.body.measurementSeriesIdentifier',
            cols: '12',
            rules: [v => !!v || v == '0' || 'rules.required'],
            dataType: {
              name: 'text',
            },
          },
          {
            name: 'verbose_name',
            translationPath: 'dialog.body.verboseName',
            cols: '12',
            rules: [v => !!v || v == '0' || 'rules.required'],
            dataType: {
              name: 'text',
            },
          },
          {
            name: 'measurement_parameter',
            translationPath: 'dialog.body.parameterType',
            cols: '12',
            dataType: {
              name: 'select',
            },
            items: this.measurementParameters,
            itemText: 'verbose_name',
            itemValue: 'name',
          },
          {
            name: 'measurement_unit',
            translationPath: 'dialog.body.measurementUnit',
            cols: '12',
            dataType: {
              name: 'text',
            },
            // items: this.computedMeasurementUnits,
            // itemText: 'text',
            // itemValue: 'text',
          },
          {
            name: 'measure_type',
            translationPath: 'dialog.body.measureType',
            cols: '12',
            rules: [v => !!v || v == '0' || 'rules.required'],
            dataType: {
              name: 'select',
            },
            items: [
              {
                value: 'current',
                text: this.$i18n.t(`dialog.body.${this.measureTypesDict['current']}`),
              },
              {
                value: 'series',
                text: this.$i18n.t(`dialog.body.${this.measureTypesDict['series']}`),
              },
            ],
          },
        ],
      };
      return measurementsActionsInputsDict[this.action].map(input => {
        return { ...input, value: JSON.parse(JSON.stringify(this.currentMeasurement?.[input.name] || '')) };
      });
    },
    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
  },
  data: () => ({
    action: '',
    actionDialogsDict: {
      add: 'MainInputDialog',
      assign: 'MainInputDialog',
      chart: 'MainLargeDialog',
      delete: 'MainDialog',
      save: 'MainInputDialog',
      unassign: 'MainDialog',
    },
    chartDataLoading: false,
    currentChartData: [],
    currentChartLabels: [],
    currentMeasurement: undefined,
    dateRange: [],
    isMainDialogLoaded: false,
    isMainDialogLoading: false,
    isMainDialogVisible: false,
    isMainInputDialogLoaded: false,
    isMainInputDialogLoading: false,
    isMainInputDialogVisible: false,
    isMainLargeDialogLoaded: false,
    isMainLargeDialogLoading: false,
    isMainLargeDialogVisible: false,
    measurementsFetched: false,
    measureTypesDict: {
      series: 'series',
      current: 'value',
    },
    measurementParameters: [],
  }),
  methods: {
    assignMeasurementToScadaPoint: call('scada/assignMeasurementToScadaPoint'),
    deleteScadaPointMeasurement: call('scada/deleteScadaPointMeasurement'),
    getMeasurementUnits: call('layers/getMeasurementUnits'),
    getMeasurementParameters: call('scada/getScadaMeasurementParameters'),
    getScadaMeasurementSeriesData: call('scada/getScadaMeasurementSeriesData'),
    getScadaPointMeasurements: call('scada/getScadaPointMeasurements'),
    postScadaPointMeasurements: call('scada/postScadaPointMeasurements'),
    putScadaPointMeasurement: call('scada/putScadaPointMeasurement'),
    unassignMeasurementToScadaPoint: call('scada/unassignMeasurementToScadaPoint'),
    async getMeasurements(id) {
      this.measurementsFetched = false;
      try {
        const r = await Promise.all([
          this.getMeasurementParameters(),
          this.getScadaPointMeasurements(id),
          this.getMeasurementUnits(),
        ]);
        this.measurementParameters = r[0];
        this.measurementsFetched = true;
      } catch {
        this.measurementsFetched = true;
      }
    },
    async handlerAdd(dialogType) {
      const measure = this.$_arrayToObject(this.measurementActionInputs, 'name', 'value');
      const data = {
        scadaPointId: this.featureId,
        payload: { measures: [measure], scada_point: this.featureId },
      };
      try {
        await this.postScadaPointMeasurements(data);
        this[`is${dialogType}Visible`] = false;
      } catch {
        //
      }
    },
    async handlerAssign(dialogType) {
      const payload = this.$_arrayToObject(this.measurementActionInputs, 'name', 'value');
      payload.measure = this.currentMeasurement.id;
      const data = {
        payload,
        scadaPointId: this.featureId,
      };
      try {
        await this.assignMeasurementToScadaPoint(data);
        this.$root.$emit('refreshTableData');
        const layersIds = this.projectLayers
          .filter(layer => layer.data_source_name === this.scadaDataSourceName)
          .map(layer => layer.id);
        if (layersIds?.length) this.$root.$emit('updateLegendStylesCounts', { layersIds });
        this.$emit('refreshObjectCard');
        this[`is${dialogType}Visible`] = false;
      } catch {
        //
      }
    },
    async handlerChart() {
      this.$refs.chart.downloadChart(this.currentMeasurement?.verbose_name);
    },
    async handlerDelete(dialogType) {
      const data = {
        scadaPointId: this.featureId,
        measurementId: this.currentMeasurement.id,
      };
      try {
        await this.deleteScadaPointMeasurement(data);
        this[`is${dialogType}Visible`] = false;
      } catch {
        //
      }
    },
    async handlerSave(dialogType) {
      const payload = this.$_arrayToObject(this.measurementActionInputs, 'name', 'value');
      const data = {
        scadaPointId: this.featureId,
        measurementId: this.currentMeasurement.id,
        payload,
      };
      try {
        await this.putScadaPointMeasurement(data);
        this[`is${dialogType}Visible`] = false;
      } catch {
        //
      }
    },
    async handlerUnassign(dialogType) {
      const data = {
        scadaPointId: this.featureId,
        relation: this.currentMeasurement.measure_attributes[0].id,
      };
      try {
        await this.unassignMeasurementToScadaPoint(data);
        this.$root.$emit('refreshTableData');
        const layersIds = this.projectLayers
          .filter(layer => layer.data_source_name === this.scadaDataSourceName)
          .map(layer => layer.id);
        if (layersIds?.length) this.$root.$emit('updateLegendStylesCounts', { layersIds });
        this.$emit('refreshObjectCard');
        this[`is${dialogType}Visible`] = false;
      } catch {
        //
      }
    },
    async prepareChartData() {
      if (this.dateRange.length !== 2) {
        return;
      }
      this.chartDataLoading = true;
      const [dateStart, dateEnd] = this.dateRange;
      const payload = {
        since: dateStart,
        until: dateEnd,
      };
      const data = {
        payload,
        measurementId: this.currentMeasurement.id,
      };
      try {
        const stats = await this.getScadaMeasurementSeriesData(data);
        this.currentChartData = stats.map(stat => stat.value);
        this.currentChartLabels = stats.map(stat => {
          const date = this.$dayjs(stat.date).format('L');
          const time = this.$dayjs(stat.date).format('LT');
          return `${date}, ${time}`;
        });
        this.chartDataLoading = false;
      } catch {
        this.chartDataLoading = false;
      }
    },
    measurementAction(action, measurement) {
      this.action = action;
      this.currentMeasurement = measurement || undefined;
      this.dateRange = [];
      this.currentChartData = [];
      this.currentChartLabels = [];
      const dialogType = this.actionDialogsDict[action];
      this[`is${dialogType}Loaded`] = true;
      this[`is${dialogType}Visible`] = true;
    },
    async measurementActionHandler() {
      const dialogType = this.actionDialogsDict[this.action];
      this[`is${dialogType}Loading`] = true;
      try {
        await this[`handler${this.capitalizeAction}`](dialogType);
        this[`is${dialogType}Loading`] = false;
      } catch {
        this[`is${dialogType}Loading`] = false;
      }
    },
  },
  watch: {
    dateRange() {
      this.prepareChartData();
    },
  },
  mounted() {
    this.$root.$off('refreshScadaWidget');
    this.$root.$on('refreshScadaWidget', () => {
      this.getMeasurements(this.featureId);
    });
    this.$watch(
      vm => [vm.isMainDialogVisible, vm.isMainInputDialogVisible],
      nV => {
        const [isMainDialogVisible, isMainInputDialogVisible] = nV;
        if (!isMainDialogVisible && !isMainInputDialogVisible) {
          this.action = '';
          this.isMainDialogLoading = false;
          this.isMainInputDialogLoading = false;
        }
      }
    );
    this.getMeasurements(this.featureId);
  },
  created() {
    this.prepareChartData = debounce(this.prepareChartData, 1000);
  },
};
</script>

<style lang="scss" scoped>
::v-deep .flex-wrap {
  flex-wrap: nowrap !important;
}
</style>
